import type {
  Flow_AbstractComponent,
  Flow_Diff,
  Flow_ComponentProps,
  Flow_ObjMap,
} from 'src/types/flow-to-typescript-codemod';
import * as React from 'react';
import classify from 'src/utils/classify';
import {UnstyledButton} from 'src/components/lib/new-button/lib-button';
import CaretIcon from 'src/images/designSystems2021/caret-down.svg?noAttrs';

import css from '../buttons.css';


type MenuButtonProps = {
  onKeyPress?:
    | ((arg1: React.KeyboardEvent<HTMLElement>) => unknown)
    | null
    | undefined;
  onClick?:
    | ((arg1: React.SyntheticEvent<HTMLElement>) => unknown)
    | null
    | undefined;
  className?: string;
  icon?: React.ReactNode;
  children?: React.ReactNode;
  type?: 'primary' | 'secondary' | 'tertiary';
  disabled?: boolean;
  size?: 'default' | 'small';
  caret?: React.ReactNode;
  isSelectable?: boolean;
  isSelected?: boolean;
};

/**
 * Our Design System menu-button. It has lots of variants.
 *
 * Set the content with children and
 * @type {[type]}
 */
export const MenuButton: Flow_AbstractComponent<
  MenuButtonProps,
  HTMLDivElement
> = React.forwardRef<HTMLDivElement, MenuButtonProps>(
  (
    {
      className,
      children,
      icon = null,
      type = 'primary',
      disabled = false,
      size = 'default',
      isSelected = false,
      isSelectable = false,
      caret = <CaretIcon />,
      onClick,
      onKeyPress,
      ...props
    }: MenuButtonProps,
    ref,
  ) => (
    <UnstyledButton
      {...props}
      onClick={onClick ?? undefined}
      onKeyPress={onKeyPress ?? undefined}
      className={classify(className, css.button, css.menuButton, {
        [css.primary]: !isSelectable && type === 'primary',
        [css.secondary]: !isSelectable && type === 'secondary',
        [css.tertiary]: type === 'tertiary',
        [css.selected]: isSelectable && isSelected,
        [css.unselected]: isSelectable && !isSelected,
        [css.disabled]: disabled,
        [css.small]: size === 'small',
      })}
      ref={ref}
    >
      {icon != null && children != null && (
        <div className={css.buttonRow}>
          <div className={css.iconContainerLeft}>{icon}</div>
          <div className={css.textContainerRight}>{children}</div>
          <div className={css.caretContainer}>{caret}</div>
        </div>
      )}

      {icon != null && !children && (
        <div className={css.buttonRow}>
          <div className={css.iconContainerCenter}>{icon}</div>
        </div>
      )}
      {icon == null && children != null && (
        <div className={css.buttonRow}>
          <div className={css.textContainerCenter}>{children}</div>
          <div className={css.caretContainer}>{caret}</div>
        </div>
      )}
    </UnstyledButton>
  ),
);

export default MenuButton;
